import React from 'react'
import { apiConfig } from '@structura/services'
import { StructuraAuthState } from './useStructuraAuth'

export interface RecentUsageSummary {
  id: string
  date: string
  analysisType: string
  spanCount: number
  loadCount: number
  durationMs: number
  success: boolean
}

export interface AccountInfo {
  userId: string
  email: string
  name?: string
  tier: string
  usageCount: number
  usageLimit: string | number
  subscriptionExpiresAt?: string
  recentUsage: RecentUsageSummary[]
  // Company Profile Fields
  portalId?: string
  companyName?: string
  logoUrl?: string
  companyPitch?: string
  contactEmail?: string
  hqLocation?: string
  serviceKeywords?: string
  sectorFocus?: string
  professionalCertifications?: string
  competence?: string
  avatarUrl?: string
  role?: string
}

type AccountState =
  | { status: 'idle' | 'unavailable' }
  | { status: 'loading' }
  | { status: 'loaded'; account: AccountInfo }
  | { status: 'error'; error: string }

export const useAccountInfo = (authState: StructuraAuthState): AccountState => {
  const [state, setState] = React.useState<AccountState>({ status: 'idle' })

  React.useEffect(() => {
    if (!authState.isSupported) {
      setState({ status: 'unavailable' })
      return
    }

    if (!authState.isAuthenticated) {
      setState({ status: 'idle' })
      return
    }

    let cancelled = false

    const fetchAccountInfo = async (): Promise<void> => {
      try {
        setState({ status: 'loading' })

        const token: string = await authState.getAccessToken()

        const response = await fetch(`${apiConfig.baseUrl}/api/user/account`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`Failed to load account information (${response.status})`)
        }

        const payload = await response.json()

        const account: AccountInfo = {
          userId: payload.data?.userId ?? '',
          email: payload.data?.email ?? '',
          name: payload.data?.name ?? undefined,
          tier: payload.data?.tier ?? 'Free',
          usageCount: Number(payload.data?.usageCount ?? 0),
          usageLimit: payload.data?.usageLimit ?? 'Unlimited',
          subscriptionExpiresAt: payload.data?.subscriptionExpiresAt ?? undefined,
          portalId: payload.data?.portalId ?? undefined,
          companyName: payload.data?.companyName ?? undefined,
          logoUrl: payload.data?.logoUrl ?? undefined,
          companyPitch: payload.data?.companyPitch ?? undefined,
          contactEmail: payload.data?.contactEmail ?? undefined,
          hqLocation: payload.data?.hqLocation ?? undefined,
          serviceKeywords: payload.data?.serviceKeywords ?? undefined,
          sectorFocus: payload.data?.sectorFocus ?? undefined,
          professionalCertifications: payload.data?.professionalCertifications ?? undefined,
          competence: payload.data?.competence ?? undefined,
          avatarUrl: payload.data?.avatarUrl ?? undefined,
          role: payload.data?.role ?? undefined,
          recentUsage: Array.isArray(payload.data?.recentUsage)
            ? payload.data.recentUsage.map((entry: any, index: number) => ({
              id: entry.id ?? entry.analysisDate ?? entry.date ?? `usage-${index}-${Math.random().toString(36).slice(2, 10)}`,
              date: entry.date ?? entry.analysisDate ?? new Date().toISOString(),
              analysisType: entry.analysisType ?? 'beam',
              spanCount: Number(entry.spanCount ?? 0),
              loadCount: Number(entry.loadCount ?? 0),
              durationMs: Number(entry.durationMs ?? 0),
              success: Boolean(entry.success ?? true)
            }))
            : []
        }

        if (!cancelled) {
          setState({ status: 'loaded', account })
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Failed to load account information.'
          setState({ status: 'error', error: message })
        }
      }
    }

    fetchAccountInfo()

    return () => {
      cancelled = true
    }
  }, [authState.isSupported, authState.isAuthenticated, authState.getAccessToken])

  return state
}


